import { useState } from "react";
import {
  Scissors, Sparkles, Sun, Droplet, MapPin, Phone, Instagram, ArrowRight, Quote, Wind, Crown,
} from "lucide-react";

import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BookingDialog } from "@/components/BookingDialog";
import { Logo } from "@/components/Logo";
import { Pricelist } from "@/components/Pricelist";
import { CertificatesSection } from "@/components/CertificatesSection";
import { ProductsCarousel } from "@/components/ProductsCarousel";

const SERVICE_CATEGORIES = [
  { id: "dekoloracija", icon: Sparkles, title: "Dekoloracija", desc: "Flamboyage, pramenovi, blajhanje — uz maksimalnu zaštitu vlasi.", from: 35 },
  { id: "bojanje", icon: Droplet, title: "Bojanje", desc: "Prirodne nijanse i moderni preljevi po vašoj mjeri.", from: 40 },
  { id: "sisanje", icon: Scissors, title: "Šišanje", desc: "Klasični i moderni rezovi za žene, muškarce i djecu.", from: 8 },
  { id: "ekstenzije", icon: Crown, title: "Ekstenzije", desc: "Ugradnja i skidanje ekstenzija.", from: 4 },
  { id: "feniranje", icon: Wind, title: "Feniranje & njega", desc: "Svakodnevni styling, peglanje, dubinski tretmani njege.", from: 7 },
  { id: "frizure", icon: Sun, title: "Svečane frizure", desc: "Frizure za vjenčanja i posebne prilike.", from: 35 },
];

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/782fa3f1-3273-4156-9240-f564fa5712f9/images/5eb64ceb3caebd6ffaa33b0efba1eb23d4e1dac89842d5c6024d5ff4113180d7.png";
// Stvarne fotografije rada — Filip Pitinac, La Maison by FP
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/qq9lpdc6_6235619e-c5f9-4056-8393-22c311569a48.jpeg";
const GALLERY_1 = "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/joxhb4dm_266bc8e7-4ce6-4654-afc7-aea4bae39f00.jpeg";
const GALLERY_2 = "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/w2xbwpnr_5afc2a91-99d5-48c3-97d7-d89bedfe9f0b.jpeg";
const GALLERY_3 = "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/qq9lpdc6_6235619e-c5f9-4056-8393-22c311569a48.jpeg";
const LOREAL_IMG = "https://images.pexels.com/photos/9146376/pexels-photo-9146376.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";

const Home = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preselected, setPreselected] = useState("");

  const openBooking = (serviceId = "") => {
    setPreselected(serviceId);
    setBookingOpen(true);
  };

  const goToPricelistCategory = (categoryId) => {
    document.getElementById("cjenik")?.scrollIntoView({ behavior: "smooth", block: "start" });
    void categoryId;
  };

  return (
    <div className="min-h-screen bg-emerald-deep text-[#F5EFE3] overflow-x-hidden" data-testid="home-page">
      <Navbar onBookClick={() => openBooking()} />

      {/* ============================ HERO ============================ */}
      <section className="relative min-h-screen flex items-center justify-center grain" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${HERO_BG})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#061A12]/85 via-[#0A2E22]/75 to-[#061A12]/95" />
        <div className="absolute inset-0 bg-[#0A2E22]/30" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24 pb-16">
          <div className="reveal">
            <span className="font-sans tracking-luxury text-[0.7rem] md:text-xs uppercase text-gold/85" data-testid="hero-tagline">
              Frizerski Atelier · Đakovo
            </span>
          </div>

          <div className="mt-10 reveal reveal-d1">
            <h1 className="sr-only">La Maison by FP — Frizerski salon Đakovo · Bojanje, Balayage, Ekstenzije</h1>
            <Logo size="lg" />
          </div>

          <p className="mt-12 font-script text-5xl md:text-6xl lg:text-7xl text-[#F5EFE3] leading-tight reveal reveal-d2" data-testid="hero-slogan">
            Vaša kosa. <span className="text-gold">Naša umjetnost.</span>
          </p>

          <p className="mt-8 max-w-2xl mx-auto font-serif text-lg md:text-xl text-[#F5EFE3]/85 leading-relaxed reveal reveal-d3" data-testid="hero-subtitle">
            Ekskluzivni <strong className="text-[#F5EFE3] font-medium">frizerski salon u Đakovu</strong> gdje kosa dobiva tretman kakav zaslužuje.
            Bojanje, balayage, pramenovi, ekstenzije i L&apos;Oréal Professionnel njega — uz strast prema detalju.
          </p>

          <p className="mt-6 font-sans tracking-luxury text-[0.65rem] md:text-xs uppercase text-gold reveal reveal-d4">
            — Ljepota je u detaljima —
          </p>

          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-5 reveal reveal-d5">
            <button
              onClick={() => openBooking()}
              data-testid="hero-book-btn"
              className="btn-luxury btn-luxury-solid"
            >
              Rezervirajte svoj termin <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => document.getElementById("usluge")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-services-btn"
              className="btn-luxury"
            >
              Naše usluge
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3 text-gold/60 animate-pulse">
          <span className="font-sans text-[0.6rem] tracking-luxury uppercase">Scroll</span>
          <div className="w-px h-12 bg-gold/40" />
        </div>
      </section>

      {/* ============================ O NAMA ============================ */}
      <section id="o-nama" className="section-pad bg-emerald-deep relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={ABOUT_IMG}
                  alt="Profesionalni rad frizerskog salona La Maison by FP — brunette Hollywood valovi, Đakovo"
                  className="w-full h-full object-cover"
                  data-testid="about-image"
                />
              </div>
              <div className="absolute -bottom-8 -right-4 md:-right-12 bg-emerald-darker border border-gold/40 px-8 py-6 hidden md:block">
                <p className="font-script text-3xl text-gold leading-none">Filip Pitinac</p>
                <p className="font-sans tracking-luxury text-[0.6rem] uppercase text-[#F5EFE3]/70 mt-2">Master Stylist · Vlasnik</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/80">— O nama</span>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-[#F5EFE3]" data-testid="about-title">
              Ljepota je u <em className="text-gold not-italic font-serif">detaljima</em>.
            </h2>
            <div className="mt-8 sep-gold" />
            <p className="mt-8 font-serif text-lg md:text-xl leading-relaxed text-[#F5EFE3]/85">
              U ugodnom i modernom ambijentu našeg salona u Đakovu, prepustite se rukama profesionalaca.
              Svakom klijentu pristupamo individualno, analizirajući potrebe vaše kose kako bismo postigli
              rezultate koji oduševljavaju.
            </p>
            <p className="mt-6 font-sans text-base text-[#B0C4B1] leading-relaxed">
              Salon vodi <span className="text-gold">Filip Pitinac</span> — sa preko 4 godine iskustva u
              frizerstvu, posvećen je tehnikama koje istovremeno donose vrhunski rezultat i čuvaju
              zdravlje vaše kose. Naš cilj nije samo lijepa frizura, već zdrava, sjajna i njegovana
              kosa koja privlači poglede.
            </p>

            <div className="mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-md">
              <div>
                <p className="font-serif text-4xl md:text-5xl text-gold font-light">4+</p>
                <p className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-[#F5EFE3]/60 mt-2">Godina iskustva</p>
              </div>
              <div>
                <p className="font-serif text-4xl md:text-5xl text-gold font-light">L&apos;Oréal</p>
                <p className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-[#F5EFE3]/60 mt-2">Pro partner</p>
              </div>
              <div>
                <p className="font-serif text-4xl md:text-5xl text-gold font-light">100%</p>
                <p className="font-sans tracking-wide-2 text-[0.65rem] uppercase text-[#F5EFE3]/60 mt-2">Individualno</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ CERTIFIKATI ============================ */}
      <CertificatesSection />

      {/* ============================ USLUGE ============================ */}
      <section id="usluge" className="section-pad bg-emerald-darker relative grain" data-testid="services-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/80">— Naše usluge</span>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl text-[#F5EFE3] leading-[1.1]" data-testid="services-title">
              Tehnike koje <em className="text-gold not-italic">čuvaju</em> integritet vaše kose.
            </h2>
            <div className="mt-8 flex justify-center"><span className="sep-gold" /></div>
            <p className="mt-8 font-serif text-lg text-[#F5EFE3]/85">
              Nudimo široku paletu frizerskih usluga koristeći isključivo provjerene tehnike i premium proizvode.
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/15">
            {SERVICE_CATEGORIES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="bg-emerald-deep p-10 md:p-12 group transition-all duration-500 hover:bg-emerald-darker"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <span className="font-serif text-gold/50 text-2xl tabular-nums">0{i + 1}</span>
                        <span className="h-px flex-1 bg-gold/20 group-hover:bg-gold/60 transition" />
                      </div>
                      <h3 className="mt-8 font-serif font-light text-2xl md:text-3xl text-[#F5EFE3] leading-tight">{s.title}</h3>
                      <p className="mt-4 font-sans text-sm md:text-base text-[#B0C4B1] leading-relaxed">{s.desc}</p>
                      <p className="mt-5 font-sans tracking-wide-2 text-[0.65rem] uppercase text-gold/70">od <span className="font-serif text-lg text-gold normal-case tracking-normal">{s.from} €</span></p>
                      <button
                        onClick={() => goToPricelistCategory(s.id)}
                        data-testid={`service-cjenik-${s.id}`}
                        className="mt-6 inline-flex items-center gap-2 font-sans tracking-luxury text-[0.7rem] uppercase text-gold hover:gap-4 transition-all duration-300"
                      >
                        Pogledaj cjenik <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                    <Icon className="w-10 h-10 md:w-12 md:h-12 text-gold/70 group-hover:text-gold group-hover:scale-110 transition-all duration-500 shrink-0" strokeWidth={1.25} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================ CJENIK ============================ */}
      <Pricelist onBook={(id) => openBooking(id)} />

      {/* ============================ METAL DETOX ============================ */}
      <section id="metal-detox" className="relative bg-emerald-deep section-pad overflow-hidden" data-testid="metal-detox-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/80">— L&apos;Oréal Professionnel</span>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl text-[#F5EFE3] leading-[1.05]" data-testid="metal-detox-title">
              Vrhunski rezultati<br />
              traže <em className="text-gold not-italic">vrhunske</em><br />
              proizvode.
            </h2>
            <div className="mt-8 sep-gold" />

            <p className="mt-8 font-serif text-lg text-[#F5EFE3]/85 leading-relaxed">
              Filip koristi cijeli <span className="text-gold">L&apos;Oréal Professionnel</span> asortiman —
              od kultne <span className="text-gold">Metal Detox</span> linije za zaštitu obojene kose,
              preko <span className="text-gold">Majirel</span> profesionalne boje i <span className="text-gold">Serie Expert</span> njege,
              do <span className="text-gold">Absolut Repair</span> obnove i <span className="text-gold">Curl Expression</span> linije za kovrče.
            </p>

            <blockquote className="mt-10 border-l border-gold/50 pl-6 py-2 relative">
              <Quote className="absolute -left-3 -top-3 w-6 h-6 text-gold/30 bg-emerald-deep p-1" strokeWidth={1.25} />
              <p className="font-serif italic text-base md:text-lg text-[#F5EFE3]/90 leading-relaxed">
                &ldquo;Metal Detox neutralizira bakar unutar vlasi, smanjuje rizik od pucanja kose i osigurava
                pouzdanu i dugotrajnu boju.&rdquo;
              </p>
            </blockquote>

            <div className="mt-12 grid grid-cols-2 gap-8">
              <div>
                <p className="font-serif text-6xl md:text-7xl text-gold font-light leading-none">87%</p>
                <p className="mt-3 font-sans tracking-wide-2 text-xs uppercase text-[#F5EFE3]/70">manje pucanja kose</p>
              </div>
              <div>
                <p className="font-serif text-6xl md:text-7xl text-gold font-light leading-none">100%</p>
                <p className="mt-3 font-sans tracking-wide-2 text-xs uppercase text-[#F5EFE3]/70">pouzdana boja</p>
              </div>
            </div>

            <button
              onClick={() => openBooking("njega-kose")}
              data-testid="metal-detox-book-btn"
              className="btn-luxury mt-12"
            >
              Zakaži Metal Detox tretman <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <ProductsCarousel />
          </div>
        </div>
      </section>

      {/* ============================ GALERIJA RADOVA ============================ */}
      <section id="galerija" className="bg-emerald-darker relative grain py-24 md:py-32 px-6" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-12 gap-12 items-end mb-14">
            <div className="md:col-span-7">
              <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/80">— Galerija radova</span>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-[#F5EFE3] leading-[1.1]" data-testid="gallery-title">
                Rezultati koji <em className="text-gold not-italic">govore</em> sami za sebe.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-serif text-base md:text-lg text-[#F5EFE3]/80 leading-relaxed">
                Stvarni radovi iz našeg salona — copper balayage, prirodni highlights i sjajne, zdrave dužine.
                Pratite nas na Instagramu za više inspiracije.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/15 border border-gold/15">
            {[GALLERY_1, GALLERY_2, GALLERY_3].map((src, i) => (
              <div key={i} className="bg-emerald-deep aspect-[4/5] overflow-hidden group relative" data-testid={`gallery-img-${i}`}>
                <img src={src} alt={`Frizerski rad — La Maison by FP, Đakovo (${i + 1}/3)`} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-darker/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="font-script text-3xl md:text-4xl text-gold">Atelier u Đakovu</p>
            <a
              href="https://instagram.com/lamaison_fp"
              target="_blank"
              rel="noreferrer"
              className="btn-luxury"
              data-testid="gallery-instagram-btn"
            >
              Pratite na Instagramu <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ============================ LOKACIJA ============================ */}
      <section id="lokacija" className="section-pad bg-emerald-deep relative" data-testid="location-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/80">— Lokacija & Kontakt</span>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl text-[#F5EFE3] leading-[1.1]" data-testid="location-title">
              Posjetite naš <em className="text-gold not-italic">atelier</em>.
            </h2>
            <div className="mt-8 sep-gold" />

            <div className="mt-10 space-y-8">
              <div className="flex items-start gap-5" data-testid="location-address">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-gold" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/70">Adresa</p>
                  <p className="mt-1 font-serif text-xl md:text-2xl text-[#F5EFE3]">Matije Gupca 6, 31400 Đakovo</p>
                </div>
              </div>

              <a href="tel:+385916127321" className="flex items-start gap-5 group" data-testid="location-phone">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center shrink-0 group-hover:bg-gold group-hover:text-emerald-darker transition">
                  <Phone className="w-5 h-5 text-gold group-hover:text-emerald-darker" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/70">Rezervacije</p>
                  <p className="mt-1 font-serif text-xl md:text-2xl text-[#F5EFE3] group-hover:text-gold transition">091 612 7321</p>
                </div>
              </a>

              <a href="https://instagram.com/lamaison_fp" target="_blank" rel="noreferrer" className="flex items-start gap-5 group" data-testid="location-instagram">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center shrink-0 group-hover:bg-gold transition">
                  <Instagram className="w-5 h-5 text-gold group-hover:text-emerald-darker" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/70">Instagram</p>
                  <p className="mt-1 font-serif text-xl md:text-2xl text-[#F5EFE3] group-hover:text-gold transition">@lamaison_fp</p>
                </div>
              </a>
            </div>

            <button
              onClick={() => openBooking()}
              data-testid="location-book-btn"
              className="btn-luxury btn-luxury-solid mt-12"
            >
              Rezervirajte termin <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="relative">
            <div className="absolute -inset-3 border border-gold/20 hidden md:block" />
            <div className="relative aspect-square overflow-hidden">
              <iframe
                title="La Maison by FP — Đakovo"
                src="https://www.google.com/maps?q=Matije+Gupca+6,+31400+Đakovo&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(1) contrast(1.05) brightness(0.85) sepia(0.2) hue-rotate(70deg)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-map"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <BookingDialog
        key={preselected || "default"}
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        preselectedService={preselected}
      />
    </div>
  );
};

export default Home;

